import styled, { createGlobalStyle, css } from "styled-components";

export const theme = {
  bg: "#f2f2f7", fg: "#111", muted: "#8a8a8e", line: "#e5e5ea", card: "#fff", danger: "#d0342c", accent: "#111", blue: "#2f6df6",
};

export const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  html, body, #root { height: 100%; }
  body { margin: 0; font: 15px/1.4 system-ui, -apple-system, sans-serif; background: ${theme.bg}; color: ${theme.fg}; }
  button, input, textarea, select { font: inherit; }
  a { color: inherit; }
`;

const buttonCss = css<{ $primary?: boolean; $small?: boolean; $danger?: boolean }>`
  padding: ${(p) => (p.$small ? "5px 12px" : "9px 16px")};
  font-size: ${(p) => (p.$small ? "13px" : "inherit")};
  font-weight: 600;
  border-radius: 999px;
  border: 0;
  background: ${theme.bg};
  color: ${(p) => (p.$danger ? theme.danger : theme.fg)};
  cursor: pointer;
  text-decoration: none;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  white-space: nowrap;
  &:disabled { opacity: .5; cursor: default; }
  ${(p) => p.$primary && css`background: ${theme.accent}; color: #fff;`}
`;
export const Button = styled.button<{ $primary?: boolean; $small?: boolean; $danger?: boolean }>`${buttonCss}`;
export const LinkButton = styled.a<{ $primary?: boolean; $small?: boolean; $danger?: boolean }>`${buttonCss}`;

/** Borderless toolbar button: icon (+ label on wide screens). 40px tall. */
export const ToolButton = styled.button<{ $on?: boolean; $danger?: boolean }>`
  height: 40px; min-width: 40px; padding: 0 10px; border: 0; border-radius: 10px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 7px; font-size: 14px; font-weight: 500; white-space: nowrap;
  background: ${(p) => (p.$on ? theme.bg : "transparent")}; color: ${(p) => (p.$danger ? theme.danger : p.$on ? theme.fg : "#333")};
  text-decoration: none;
  &:hover { background: ${theme.bg}; }
  &:disabled { opacity: .4; cursor: default; }
  svg { flex: none; }
  @media (max-width: 900px) { .label { display: none; } padding: 0 8px; }
`;

export const Input = styled.input`
  padding: 10px 12px; border: 1px solid ${theme.line}; border-radius: 10px; background: ${theme.card};
`;

export const Muted = styled.div`color: ${theme.muted}; font-size: 13px;`;

export const Badge = styled.span`
  display: inline-block; min-width: 18px; padding: 0 5px; border-radius: 9px; background: #e5322d; color: #fff;
  font-size: 11px; font-weight: 700; line-height: 18px; text-align: center;
`;

export const Avatar = styled.img<{ $size?: number }>`
  width: ${(p) => p.$size ?? 40}px; height: ${(p) => p.$size ?? 40}px; border-radius: 50%; background: #eee; cursor: pointer; flex: none; object-fit: cover;
`;

export const StatusPill = styled.span<{ $status?: string }>`
  font-size: 11px; padding: 1px 6px; border-radius: 6px; white-space: nowrap; flex: none; align-self: center; line-height: 1.6;
  background: ${(p) => ({ generating: "#ffe9a8", error: "#ffc8c8", ready: "#cdf5d4" } as Record<string, string>)[p.$status || ""] || "#eee"};
`;

export const Spinner = styled.div<{ $small?: boolean }>`
  width: ${(p) => (p.$small ? 14 : 36)}px; height: ${(p) => (p.$small ? 14 : 36)}px; flex: none; border-radius: 50%;
  border: ${(p) => (p.$small ? 2 : 4)}px solid ${theme.line}; border-top-color: ${theme.fg};
  animation: spin .9s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export const ErrorBox = styled.div`
  color: ${theme.danger}; padding: 6px 14px; background: #fff3f3; font-size: 13px; white-space: pre-wrap;
`;

// ---- grouped lists (iOS "inset grouped" form style) ----

/** Section heading above a Group. */
export const GroupTitle = styled.h3`
  font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: ${theme.muted}; margin: 22px 16px 6px;
  display: flex; align-items: center; gap: 8px;
  &:first-child { margin-top: 6px; }
`;
/** A card holding Rows separated by hairlines. Children are borderless. */
export const Group = styled.div`
  background: ${theme.card}; border-radius: 14px; overflow: hidden;
  > * + * { border-top: 1px solid ${theme.line}; }
  > * { margin-left: 16px; padding-right: 16px; }
`;
/** One list row: content on the left, a control/value on the right. */
export const Row = styled.div<{ $tappable?: boolean }>`
  display: flex; align-items: center; gap: 12px; min-height: 48px; padding: 8px 0; padding-right: 16px;
  ${(p) => p.$tappable && css`cursor: pointer; &:active { background: ${theme.bg}; }`}
  .grow { flex: 1; min-width: 0; }
  .label { flex: none; color: ${theme.muted}; min-width: 56px; }
  .value { color: ${theme.muted}; margin-left: auto; text-align: right; }
  .name { font-weight: 600; }
  .sub { color: ${theme.muted}; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;
/** Borderless text field for use inside a Row (label on the left, field fills the rest). */
export const RowInput = styled.input`
  flex: 1; min-width: 0; border: 0; background: transparent; padding: 6px 0; outline: none; text-align: left;
  font-weight: 600; color: ${theme.fg};
  &::placeholder { color: ${theme.muted}; font-weight: 400; }
`;
/** Row that is a single action (Sign out, Admin…). */
export const ActionRow = styled.button<{ $danger?: boolean; $center?: boolean }>`
  display: flex; align-items: center; gap: 10px; width: calc(100% - 16px); min-height: 48px; padding: 8px 16px 8px 0; border: 0; background: none; cursor: pointer;
  color: ${(p) => (p.$danger ? theme.danger : theme.blue)}; text-align: left; text-decoration: none; font-weight: 500;
  ${(p) => p.$center && css`justify-content: center; text-align: center;`}
  &:active { background: ${theme.bg}; }
`;

/** iOS-style toggle. */
const SwitchBox = styled.label`
  position: relative; width: 51px; height: 31px; flex: none; cursor: pointer;
  input { opacity: 0; width: 0; height: 0; position: absolute; }
  .track { position: absolute; inset: 0; border-radius: 999px; background: #e9e9eb; transition: background .15s; }
  .knob { position: absolute; top: 2px; left: 2px; width: 27px; height: 27px; border-radius: 50%; background: #fff; box-shadow: 0 2px 5px rgba(0,0,0,.25); transition: transform .15s; }
  input:checked ~ .track { background: #34c759; }
  input:checked ~ .knob { transform: translateX(20px); }
`;
export function Switch({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <SwitchBox>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" /><span className="knob" />
    </SwitchBox>
  );
}

/** Placeholder rows while a list loads. */
const Bone = styled.div`
  display: flex; align-items: center; gap: 12px; min-height: 56px; padding: 8px 0;
  .c { width: 40px; height: 40px; border-radius: 50%; }
  .l { height: 12px; border-radius: 6px; }
  .c, .l { background: linear-gradient(90deg, #eee 25%, #f6f6f6 50%, #eee 75%); background-size: 400% 100%; animation: shimmer 1.4s infinite; }
  @keyframes shimmer { from { background-position: 100% 0; } to { background-position: 0 0; } }
`;
export function Skeleton({ rows = 3 }: { rows?: number }) {
  return <>{Array.from({ length: rows }, (_, i) => (
    <Bone key={i}><div className="c" /><div style={{ flex: 1 }}><div className="l" style={{ width: `${45 + (i * 17) % 30}%`, marginBottom: 8 }} /><div className="l" style={{ width: `${25 + (i * 11) % 20}%` }} /></div></Bone>
  ))}</>;
}

/** Page header for the host tabs. */
export const PageTitle = styled.h1`font-size: 28px; letter-spacing: -.02em; margin: 6px 16px 10px; display: flex; align-items: center; gap: 10px; .spacer { flex: 1; }`;

// ---- bottom sheet ----
const SheetBg = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.35); display: flex; align-items: flex-end; justify-content: center; z-index: 10;
`;
const SheetBox = styled.div`
  width: 100%; max-width: 480px; max-height: 80vh; overflow: auto; background: ${theme.bg}; border-radius: 18px 18px 0 0; padding: 18px 16px calc(18px + env(safe-area-inset-bottom));
  h2 { margin: 0 0 12px; font-size: 20px; }
`;
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <SheetBg onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <SheetBox><h2>{title}</h2>{children}</SheetBox>
    </SheetBg>
  );
}

/** `matches` for a media query, live. */
import { useEffect, useState } from "react";
export function useMediaQuery(q: string): boolean {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia(q).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    on(); mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [q]);
  return m;
}
